import React from 'react';
import { Helmet } from 'react-helmet-async';
import { faqSchemas, aggregateRatingSchema, generateReviewSchema } from '../data/seoSchemas';

const EnhancedSEO = ({ 
  title,
  description,
  keywords,
  image,
  url,
  trade, // e.g., 'plumber', 'electrician'
  includeFAQ = false,
  includeRating = true,
  testimonials = [], // Array of testimonials for review schema
  breadcrumbs = [], // Array of {name, url}
  additionalSchemas = []
}) => {
  const siteUrl = "https://localbusinessheroes.net";
  const fullUrl = url ? `${siteUrl}${url}` : siteUrl;
  
  const schemas = [];
  
  // Add FAQ Schema if requested
  if (includeFAQ && trade && faqSchemas[trade]) {
    schemas.push(faqSchemas[trade]);
  }
  
  // Add Aggregate Rating Schema
  if (includeRating) {
    schemas.push(aggregateRatingSchema);
  }
  
  // Add individual review schemas (limit to first 5 for performance)
  if (testimonials && testimonials.length > 0) {
    testimonials.slice(0, 5).forEach(testimonial => {
      schemas.push(generateReviewSchema(testimonial));
    });
  }
  
  // Add Breadcrumb Schema
  if (breadcrumbs && breadcrumbs.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": crumb.name,
        "item": crumb.url ? `${siteUrl}${crumb.url}` : undefined
      }))
    });
  }
  
  // Add any additional custom schemas
  if (additionalSchemas && additionalSchemas.length > 0) {
    schemas.push(...additionalSchemas);
  }

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {image && <meta property="og:image" content={image} />}
      <meta property="og:site_name" content="Local Business Heroes" />
      <meta property="og:locale" content="en_GB" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={fullUrl} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      {image && <meta property="twitter:image" content={image} />}

      {/* Additional SEO */}
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="geo.region" content="GB" />
      <meta name="geo.placename" content="United Kingdom" />
      <meta name="author" content="Local Business Heroes" />

      {/* Schema Markup */}
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default EnhancedSEO;
